import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured
import './Auth.css';

/**
 * Login/Signup Component - 2025 Design
 *
 * Clean, minimal design following Apple/Google standards
 * Official Tappy brand colors
 * No emojis - professional SVG icons only
 */

// SVG Icons
const Icons = {
  Eye: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  EyeOff: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>
      <line x1="2" x2="22" y1="2" y2="22"/>
    </svg>
  ),
  Clever: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
  ),
};

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [anonymousId, setAnonymousId] = useState('');
  const [isLibrarySignup, setIsLibrarySignup] = useState(false);
  const [libraryName, setLibraryName] = useState('');
  const [voiceSessionSource, setVoiceSessionSource] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Free-forever teacher tier signup (FREE_TEACHER_TIER_SCOPE.md Day 1)
  const [signupRole, setSignupRole] = useState(''); // '', 'student', 'teacher', 'parent'
  const [ferpaAttested, setFerpaAttested] = useState(false);
  // Day 5 attribution adjustments — optional cluster + acquisition data
  const [schoolName, setSchoolName] = useState('');
  const [referralSource, setReferralSource] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  // Post-auth destinations live on the app host (app.teachtappy.ai). On the
  // apex/marketing domain those routes don't exist — the router catch-all
  // sent fresh signups back to the homepage looking logged-out (double-login
  // bug). Auth cookies are domain-scoped (.teachtappy.ai), so a hard hop to
  // the app host carries the session.
  const goToApp = (path) => {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('app.')) {
      navigate(path);
    } else {
      window.location.href = `https://app.${host.replace(/^www\./, '')}${path}`;
    }
  };

  // Parse URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'signup') setIsSignupMode(true);
    if (params.get('anon')) setAnonymousId(params.get('anon'));
    if (params.get('email')) setEmail(params.get('email'));
    if (params.get('library') === 'true' || params.get('tier') === 'free') {
      setIsLibrarySignup(true);
      setIsSignupMode(true);
    }
    if (params.get('source')) setVoiceSessionSource(params.get('source'));
    if (params.get('invite_token')) setInviteToken(params.get('invite_token'));
    if (params.get('invitation')) {
      setInviteToken(params.get('invitation'));
      setIsSignupMode(true);
    }
    if (params.get('tier')) setSelectedTier(params.get('tier'));
    // Free-forever teacher tier entry point: /signup?role=teacher
    if (params.get('role')) {
      const roleParam = params.get('role');
      if (['student', 'teacher', 'parent'].includes(roleParam)) {
        setSignupRole(roleParam);
        setIsSignupMode(true);
      }
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Gate: teacher signup requires FERPA attestation before submit
    if (isSignupMode && signupRole === 'teacher' && !ferpaAttested) {
      setError('Please confirm the FERPA attestation to continue.');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isSignupMode ? '/api/auth/signup' : '/api/auth/login';

      const payload = { username, password };

      if (isSignupMode) {
        payload.email = email;
        if (isLibrarySignup && libraryName.trim()) {
          payload.account_type = 'library';
          payload.library_name = libraryName.trim();
        }
        if (anonymousId) payload.anonymous_id = anonymousId;
        if (voiceSessionSource) payload.source = voiceSessionSource;
        if (inviteToken) payload.invitation_code = inviteToken;
        // Free-forever teacher tier — role + FERPA attestation
        if (signupRole) payload.role = signupRole;
        if (signupRole === 'teacher' && ferpaAttested) {
          payload.ferpa_attestation = {
            attested_at: new Date().toISOString(),
            version: 'v1',
          };
        }
        // Day 5 attribution — optional school + referral source.
        // Sent only on teacher signups; backend ignores them otherwise.
        if (signupRole === 'teacher') {
          if (schoolName.trim()) payload.school_name = schoolName.trim();
          if (referralSource) payload.referral_source = referralSource;
        }
      }

      const response = await axios.post(endpoint, payload);
      const { user } = response.data;

      localStorage.setItem('user_info', JSON.stringify(user));
      if (anonymousId) localStorage.removeItem('morpheus_anon_id');

      // Track in PostHog
      try {
        posthog.identify(user.email || user.username);
        posthog.capture(isSignupMode ? 'user_signup_completed' : 'user_login', {
          user_id: user.id,
          converted_from_anonymous: Boolean(anonymousId),
          tier: user.subscription?.tier || 'student'
        });
      } catch (err) {}

      // Handle redirects
      // Special handling for learning_snapshot source - user is converting from anonymous guest
      const isConvertingFromLearningSnapshot = voiceSessionSource === 'learning_snapshot';
      // Teacher free-forever signup bypasses paywall → teacher onboarding flow
      const isTeacherSignup = isSignupMode && signupRole === 'teacher';
      const shouldRedirectToUpgrade = (
        isSignupMode
        && !isLibrarySignup
        && !isConvertingFromLearningSnapshot
        && !isTeacherSignup
      );

      // Transfer guest progress if converting from anonymous
      if (anonymousId) {
        try {
          await axios.post('/api/user/transfer-guest-progress', {
            user_id: user.id,
            anonymous_id: anonymousId,
            guest_data: {
              tokens_earned: localStorage.getItem('guest_tokens') || 0,
              session_interactions: localStorage.getItem('guest_session_interactions') || 0,
              voice_minutes_used: localStorage.getItem('morpheus_free_voice_minutes_used') || 0,
              mira_orb_color: localStorage.getItem('mira_orb_color') || null,
              learning_style_weights: localStorage.getItem('learning_style_weights') || null,
            }
          }, { withCredentials: true });

          // Clear anonymous localStorage after successful transfer
          localStorage.removeItem('guest_tokens');
          localStorage.removeItem('guest_session_interactions');
          // Keep mira_orb_color - it's also used by authenticated users
        } catch (transferError) {
          console.error('Failed to transfer guest progress:', transferError);
        }
      }

      if (isTeacherSignup) {
        // Free-forever teacher tier — skip paywall, route straight to onboarding
        onLoginSuccess(user);
        goToApp('/teacher/onboarding?welcome=true');
      } else if (isConvertingFromLearningSnapshot) {
        // User came from LearningSnapshot after using free sessions
        // They get 4 more free sessions as a registered user - go to tutor
        onLoginSuccess(user);
        goToApp('/tutor?welcome=true&converted=true');
      } else if (shouldRedirectToUpgrade) {
        onLoginSuccess(user);
        const tierParam = selectedTier ? `&tier=${selectedTier}` : '';
        goToApp(`/upgrade?source=signup${tierParam}`);
      } else if (isSignupMode && isLibrarySignup && libraryName.trim()) {
        onLoginSuccess(user);
        goToApp('/guest-tutor?library=true&welcome=true');
      } else if (isSignupMode && selectedTier === 'free') {
        onLoginSuccess(user);
        goToApp('/guest-tutor?welcome=true');
      } else {
        onLoginSuccess(user);
        if (user.role === 'teacher' || user.role === 'administrator') {
          goToApp('/teacher/dashboard');
        } else if (
          // Parent/plan owner: has home subscription AND is NOT linked to another plan
          (user.subscription?.tier === 'home' || user.subscription?.tier === 'expert-family') &&
          !user.family_plan_id
        ) {
          goToApp('/parent');
        } else {
          goToApp('/tutor');
        }
      }

      setLoading(false);
    } catch (error) {
      // Friendly handling for the teacher-tier waitlist gate (503).
      // The backend returns code='teacher_tier_waitlist' when the
      // ENABLE_TEACHER_FREE_TIER flag is off.
      if (error.response?.data?.code === 'teacher_tier_waitlist') {
        setError(
          error.response.data.error ||
            "Tappy's free teacher tier is rolling out gradually."
        );
      } else {
        setError(
          error.response?.data?.error || 'An error occurred. Please try again.'
        );
      }
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignupMode(!isSignupMode);
    setError('');
  };

  const handleContinueAsGuest = () => {
    try {
      posthog.capture('continued_as_guest', {
        from_page: isSignupMode ? 'signup' : 'login'
      });
    } catch (err) {}

    const isAppSubdomain = window.location.hostname.startsWith('app.') &&
                          !window.location.hostname.includes('localhost');

    if (isAppSubdomain) {
      const mainDomain = window.location.hostname.replace('app.', '');
      window.location.href = `https://${mainDomain}`;
    } else {
      navigate('/');
    }
  };

  const handleCleverLogin = () => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/clever/login';
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/TAPPY-logo-black.png" alt="Tappy" />
        </div>

        {/* Header */}
        <div className="auth-header">
          <h1 className="auth-title">
            {isSignupMode ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="auth-subtitle">
            {isSignupMode
              ? 'Start learning with Mira, your AI tutor'
              : 'Sign in to continue learning'}
          </p>
        </div>

        {/* Error */}
        {error && <div className="auth-error">{error}</div>}

        {/* Anonymous progress notice */}
        {isSignupMode && anonymousId && (
          <div className="auth-info">
            Your progress will be saved to your new account.
          </div>
        )}

        {/* Selected tier preview */}
        {isSignupMode && selectedTier && selectedTier !== 'free' && (
          <div className="auth-tier-preview">
            <p className="auth-tier-label">Selected plan</p>
            <p className="auth-tier-name">{selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)}</p>
          </div>
        )}

        {/* Role selector — free-forever teacher tier entry (hidden for invited signups) */}
        {isSignupMode && !isLibrarySignup && !inviteToken && (
          <div className="auth-role-selector">
            <p className="auth-role-label">I'm signing up as a...</p>
            <div className="auth-role-options">
              {[
                { value: 'student', label: 'Student' },
                { value: 'teacher', label: 'Teacher' },
                { value: 'parent', label: 'Parent' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`auth-role-option${signupRole === opt.value ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="signupRole"
                    value={opt.value}
                    checked={signupRole === opt.value}
                    onChange={(e) => {
                      setSignupRole(e.target.value);
                      // Reset attestation on role change
                      if (e.target.value !== 'teacher') setFerpaAttested(false);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form className="auth-form" onSubmit={handleSubmit}>
          {/* Username */}
          <div className="auth-input-group">
            <label className="auth-label" htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              className="auth-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          {/* Email (signup only) */}
          {isSignupMode && (
            <div className="auth-input-group">
              <label className="auth-label" htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
          )}

          {/* Library name (library signup only) */}
          {isSignupMode && isLibrarySignup && (
            <div className="auth-input-group">
              <label className="auth-label" htmlFor="libraryName">Library name (optional)</label>
              <input
                type="text"
                id="libraryName"
                className="auth-input"
                value={libraryName}
                onChange={(e) => setLibraryName(e.target.value)}
                placeholder="e.g., Springfield Public Library"
              />
            </div>
          )}

          {/* Password */}
          <div className="auth-input-group">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor="password">Password</label>
              {!isSignupMode && (
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="auth-input auth-input-with-toggle"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={isSignupMode ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
          </div>

          {/* Optional school + referral source — teachers only.
              Day 5 attribution adjustments. Both fields are optional and
              do not gate signup. School powers Phase-2 cluster detection;
              referral source powers acquisition-channel attribution. */}
          {isSignupMode && signupRole === 'teacher' && (
            <>
              <div className="auth-input-group">
                <label className="auth-label" htmlFor="school-name">
                  School name <span className="auth-label-optional">(optional)</span>
                </label>
                <input
                  id="school-name"
                  className="auth-input"
                  type="text"
                  value={schoolName}
                  maxLength={120}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g. Lincoln Middle School"
                  autoComplete="organization"
                />
              </div>
              <div className="auth-input-group">
                <label className="auth-label" htmlFor="referral-source">
                  How did you hear about Tappy? <span className="auth-label-optional">(optional)</span>
                </label>
                <select
                  id="referral-source"
                  className="auth-input"
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                >
                  <option value="">Select one…</option>
                  <option value="colleague">A colleague</option>
                  <option value="search">Search engine</option>
                  <option value="social">Social media</option>
                  <option value="conference">Conference / workshop</option>
                  <option value="clever">Clever</option>
                  <option value="district">My school or district</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </>
          )}

          {/* FERPA attestation — required for free-forever teacher tier */}
          {isSignupMode && signupRole === 'teacher' && (
            <div className="auth-attestation">
              <label className="auth-attestation-label">
                <input
                  type="checkbox"
                  checked={ferpaAttested}
                  onChange={(e) => setFerpaAttested(e.target.checked)}
                />
                <span>
                  I am authorized by my school to use Tappy with my students,
                  and I confirm materials I upload are school-approved.
                </span>
              </label>
              <p className="auth-attestation-hint">
                Tappy's free tier collects only first names for students.
                See our <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">privacy policy</a>.
              </p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading}
          >
            {loading
              ? (isSignupMode ? 'Creating account...' : 'Signing in...')
              : (isSignupMode ? 'Create account' : 'Sign in')}
          </button>
        </form>

        {/* Divider */}
        <div className="auth-divider">
          <div className="auth-divider-line" />
          <span className="auth-divider-text">or</span>
          <div className="auth-divider-line" />
        </div>

        {/* Clever SSO */}
        <button type="button" className="auth-btn-sso" onClick={handleCleverLogin}>
          <Icons.Clever />
          Continue with Clever
        </button>
        <p className="auth-sso-hint">For students and teachers using Clever</p>

        {/* Footer */}
        <div className="auth-footer">
          <p className="auth-footer-text">
            {isSignupMode ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button type="button" className="auth-footer-link" onClick={toggleMode}>
              {isSignupMode ? 'Sign in' : 'Create account'}
            </button>
          </p>

          <button
            type="button"
            className="auth-btn-secondary"
            onClick={handleContinueAsGuest}
          >
            Continue as guest
          </button>
        </div>

        {/* Legal */}
        {isSignupMode && (
          <div className="auth-legal">
            By creating an account, you agree to our{' '}
            <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms</a>
            {' '}and{' '}
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
